import { Alert, Form, Modal, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { TextArea } from "../../../components/Input/TextArea.tsx"
import { firstErrorMessage, voidEnvelope } from "../api/esign.services.ts"

const REASON_LIMIT = 500

interface VoidRequestModalProps {
  envelopeId: string
  documentName: string
  onCancel: () => void
  onVoided: () => void
}

export function VoidRequestModal({
  envelopeId,
  documentName,
  onCancel,
  onVoided
}: VoidRequestModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<{ reason: string }>()
  const [saving, setSaving] = useState(false)
  const [refusal, setRefusal] = useState<string | null>(null)

  const submit = async () => {
    let reason: string
    try {
      reason = (await form.validateFields()).reason.trim()
    } catch {
      return
    }
    setSaving(true)
    setRefusal(null)
    const result = await voidEnvelope(envelopeId, reason)
    setSaving(false)
    if (result.ok) {
      onVoided()
      return
    }
    setRefusal(firstErrorMessage(result) ?? t("esign.lifecycle.voidFailed"))
  }

  return (
    <Modal
      open
      title={
        <span className={"block pr-6"}>
          {t("esign.lifecycle.voidTitle", { document: documentName })}
        </span>
      }
      okText={t("esign.lifecycle.voidConfirm")}
      cancelText={t("esign.common.cancel")}
      okButtonProps={{ danger: true }}
      confirmLoading={saving}
      cancelButtonProps={{ disabled: saving }}
      closable={!saving}
      maskClosable={false}
      onOk={submit}
      onCancel={onCancel}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">
        {t("esign.lifecycle.voidExplain")}
      </Typography.Paragraph>
      {refusal && (
        <Alert type="error" showIcon className="mb-4" message={refusal} />
      )}
      <Form form={form} layout="vertical" disabled={saving}>
        <Form.Item
          name="reason"
          label={t("esign.lifecycle.voidReason")}
          rules={[
            {
              required: true,
              whitespace: true,
              message: t("esign.lifecycle.voidReasonRequired")
            }
          ]}
          // showCount hangs the counter below the field, outside the layout.
          className="pb-4"
        >
          <TextArea
            rows={3}
            maxLength={REASON_LIMIT}
            showCount
            placeholder={t("esign.lifecycle.voidReasonPlaceholder")}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
